import { useTranslation } from 'react-i18next';
import PageTitle from '@/components/common/PageTitle';
import { formatMoney } from '@/lib/format';
import classes from './DealPreviewHead.module.css';

export interface DealPreviewPrice {
  /** Подпись цены: «Цена с доставкой (CPT)» у покупателя, «Вы получаете (FCA, у ворот)» у продавца. */
  readonly label: string;
  /** Цена сделки, ₽/т: та, что показана, — по ней же считается разница с моей ценой. */
  readonly value: number;
  readonly unit: string;
  /** Моя цена из ссылки, ₽/т; нет — разница не выводится. */
  readonly myPrice?: number;
  /** Итог за объём сделки. */
  readonly total?: string;
  /** Цена пересчитывается: значение на экране устарело. */
  readonly busy?: boolean;
}

interface DealPreviewHeadProps {
  /** Сторона сделки: покупателю выгодна цена ниже его цены, продавцу — выше. */
  readonly side: 'buy' | 'sell';
  readonly title: string;
  /** Вводная строка под заголовком: чьё предложение, качество, урожай. */
  readonly lead?: string;
  readonly price?: DealPreviewPrice;
}

// Шапка предпросмотра сделки («Бюллетень», образец b-deal): слева культура и вводная строка, справа цена с разницей
// к моей цене и итогом. Одна для обеих сторон сделки: покупатель и продавец видят одну сделку одинаково.
export default function DealPreviewHead({ side, title, lead, price }: DealPreviewHeadProps) {
  const { t } = useTranslation();
  // Разница считается от показанной цены, а не берётся из ссылки: после «условия изменились» (412) или при расхождении
  // с ценой на момент построения ссылки строка иначе говорила бы о прежней цене.
  const diff = price?.myPrice != null ? Math.round(price.value - price.myPrice) : 0;
  const prefix = side === 'buy' ? 'app.buy.offer' : 'app.sell.deal';
  const compare =
    price?.myPrice != null && diff !== 0
      ? {
          tone: (side === 'buy') === diff < 0 ? 'gain' : 'loss',
          text: t(`${prefix}.${diff > 0 ? 'aboveMine' : 'belowMine'}`, {
            diff: formatMoney(Math.abs(diff)),
            price: formatMoney(price.myPrice),
          }),
        }
      : undefined;
  return (
    <div className={classes.head}>
      <div style={{ minWidth: 0 }}>
        <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
          {title}
        </PageTitle>
        {lead ? <p className={classes.lead}>{lead}</p> : null}
      </div>
      {price ? (
        <div className={classes.price} aria-busy={price.busy || undefined}>
          <p className={classes.priceLabel}>{price.label}</p>
          <p className={classes.priceValue}>
            {formatMoney(price.value)} <span className={classes.priceUnit}>{price.unit}</span>
          </p>
          {compare ? <p className={compare.tone === 'gain' ? classes.gain : classes.loss}>{compare.text}</p> : null}
          {price.total ? <p className={classes.priceTotal}>{price.total}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
